import styles from './BlockFilters.module.css'
import FiltersIcon from '../../../assets/filter-primary.svg?react'
import CheckOrderingIcon from '../../../assets/ordering-checked.svg?react'
import UncheckOrderingIcon from '../../../assets/ordering-unchecked.svg?react'
import CloseSecondary from '../../../assets/close-secondary.svg?react'
import MyLocation from '../../../assets/my-location.svg?react'
import Flag from '../../../assets/flag.svg?react'
import ExpandLess from '../../../assets/expand-less.svg?react'
import ToggleFiltersClose from '../../../assets/toggle-filters-close.svg?react'
import ToggleFiltersOpen from '../../../assets/toggle-filters-open.svg?react'
import { useTranslation } from 'react-i18next'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import ReactSwitch from 'react-switch'
import { useComuni } from '../../../hooks/comuni'
import { useDetectClickOutside } from 'react-detect-click-outside'
import { getActiveGeocontext } from '../../../utils'
import { useAuthUser } from 'use-eazy-auth'
import { useLocation } from 'react-router-dom'
import { useProvince } from '../../../hooks/province'
import { Geocontext, Provincia, Regione } from '../../../types'
import { useRegioni } from '../../../hooks/regioni'

interface Props {
  setFilters: (filters: any) => void
  filters: Record<string, any>
  setDetail: (detail: any) => void
  setComuneFilterCoords?: (coords: number[]) => void
  setProvinciaFilterCoords?: (coords: number[]) => void
  setRegioneFilterCoords?: (coords: number[]) => void
  isResized?: boolean
  setFiltersWidth?: (width: number) => void
}

type Comune = {
  id: string | number
  code: string
  name: string
  centroid: { coordinates: number[] }
}

type Category = {
  id: string | number
  name: string
  order: number
  icon: { icon_color: string }
  translation?: string
}

const DEFAULT_FILTERS = {
  app_category: '',
  municipality: '',
  province: '',
  region: '',
  in_contest: 'true',
  only_without_pictures: '',
  without_coordinates: '',
  only_disabled: '',
  last_image_before_5_years: '',
}

/* -------------------------------------------------------------------------- */
/*                               Sub-components                               */
/* -------------------------------------------------------------------------- */

type RegioneAutocompleteProps = {
  value: string
  setValue: (v: string) => void
  openOptions: boolean
  setOpenOptions: (v: boolean) => void

  filters: Record<string, any>
  setFilters: (filters: any) => void
  setDetail: (detail: any) => void
  setRegioneFilterCoords?: (coords: number[]) => void

  activeGeocontextId?: string | number
  activeGeocontext?: Geocontext
}

function RegioneAutocomplete({
  value,
  setValue,
  openOptions,
  setOpenOptions,
  filters,
  setFilters,
  setDetail,
  setRegioneFilterCoords,
  activeGeocontextId,
  activeGeocontext,
}: RegioneAutocompleteProps) {
  const { t } = useTranslation()

  const filtersRegione = useMemo(() => {
    const trimmed = value.trim()
    if (trimmed) return { q: trimmed }
    if (filters.region !== '') return { q: '', id: filters.region }
    return {}
  }, [value, filters.region])

  const { data: regioni, isFetching: isLoadingRegioni } = useRegioni(
    Number(activeGeocontextId),
    filtersRegione
  )
  const regioniFiltered = useMemo(() => {
    const trimmed = value.trim()
    if (!trimmed) return []

    const list = (regioni as Regione[] | undefined) ?? []
    const lowered = trimmed.toLowerCase()

    const filtered = list.filter((c) => c.name.toLowerCase().includes(lowered))

    return filtered.sort((a, b) => {
      const aStarts = a.name.toLowerCase().startsWith(lowered)
      const bStarts = b.name.toLowerCase().startsWith(lowered)
      if (aStarts && !bStarts) return -1
      if (!aStarts && bStarts) return 1
      return a.name.length - b.name.length
    })
  }, [value, regioni])

  // Sync testo con province (es. set esterno / deep link)
  useEffect(() => {
    if (!filters.region) return
    const list = (regioni as Regione[] | undefined) ?? []
    const regione = list.find((c) => String(c.id) === String(filters.region))
    setValue(regione?.name || '')
  }, [filters.region, regioni, setValue])

  // se regioni torna vuoto, pulisco testo
  useEffect(() => {
    if (filters.region === '') setValue('')
  }, [filters.region, setValue])

  const clearRegione = useCallback(() => {
    setValue('')
    setFilters({ ...filters, region: '' })
    setOpenOptions(false)
  }, [filters, setFilters, setOpenOptions, setValue])

  const centerOnRegione = useCallback(() => {
    const list = (regioni as Regione[] | undefined) ?? []
    const regione = list.find(
      (c) => c.name.toLowerCase() === value.toLowerCase()
    )
    if (regione && setRegioneFilterCoords) {
      setRegioneFilterCoords(regione.centroid.coordinates)
    }
    setOpenOptions(false)
  }, [regioni, setRegioneFilterCoords, setOpenOptions, value])

  const onSelectRegione = useCallback(
    (regione: Regione) => {
      setDetail(null)
      setValue(regione.name)
      setOpenOptions(false)
      setFilters({
        ...filters,
        region: regione.id,
        province: '',
        municipality: '',
        monument_id: '',
        monument_lat: '',
        monument_lon: '',
      })
      if (setRegioneFilterCoords) {
        setRegioneFilterCoords(regione.centroid.coordinates)
      }
    },
    [
      filters,
      setDetail,
      setFilters,
      setRegioneFilterCoords,
      setOpenOptions,
      setValue,
    ]
  )

  return (
    <div className={styles.FilterComune}>
      <div className={styles.FilterLabel}>
        {t(activeGeocontext?.region_name ?? 'FIL_BOX_REGION')}
      </div>

      <div className="w-100 position-relative">
        <input
          className={styles.InputComune}
          type="text"
          placeholder={t(activeGeocontext?.region_placeholder ?? '')}
          value={value}
          onChange={(e) => {
            setOpenOptions(true)
            setValue(e.target.value)
          }}
        />

        {filters.province === '' &&
          filters.municipality === '' &&
          filters.region !== '' && (
            <div
              title={t('FIL_BOX_CENTER_REGION')}
              className={styles.PointComune}
              onClick={centerOnRegione}
            >
              <MyLocation />
            </div>
          )}

        {value.length > 0 && (
          <div className={styles.CloseIconWrapper} onClick={clearRegione}>
            <CloseSecondary className={styles.CloseIcon} />
          </div>
        )}
      </div>

      {openOptions && value.trim().length > 0 && regioniFiltered.length > 0 && (
        <div className={styles.ComuniList}>
          {regioniFiltered.map((regione) => (
            <div
              key={regione.code}
              className={styles.ComuneItem}
              onClick={() => onSelectRegione(regione)}
            >
              <div className={styles.ComuneItemTitle}>
                <Flag className="me-2" /> {regione.name}
              </div>
            </div>
          ))}
        </div>
      )}
      {openOptions &&
        value.trim().length > 0 &&
        regioniFiltered.length === 0 &&
        !isLoadingRegioni && (
          <div className={styles.NoResults}>{t('FIL_BOX_NO_REGION')}</div>
        )}
    </div>
  )
}

type ProvinciaAutocompleteProps = {
  value: string
  setValue: (v: string) => void
  openOptions: boolean
  setOpenOptions: (v: boolean) => void

  filters: Record<string, any>
  setFilters: (filters: any) => void
  setDetail: (detail: any) => void
  setProvinciaFilterCoords?: (coords: number[]) => void

  activeGeocontextId?: string | number
  activeGeocontext?: Geocontext
}

function ProvinciaAutocomplete({
  value,
  setValue,
  openOptions,
  setOpenOptions,
  filters,
  setFilters,
  setDetail,
  setProvinciaFilterCoords,
  activeGeocontextId,
  activeGeocontext,
}: ProvinciaAutocompleteProps) {
  const { t } = useTranslation()

  // dentro ProvinciaAutocomplete

  const filtersProvince = useMemo(() => {
    const trimmed = value.trim()

    // base payload
    const base: Record<string, any> = {}

    // ✅ se c'è la regione selezionata, passala al fetch
    if (filters.region !== '') {
      base.region = filters.region
    }

    if (trimmed) return { ...base, q: trimmed }
    if (filters.province !== '') return { ...base, q: '', id: filters.province }
    return base
  }, [value, filters.province, filters.region])

  const { data: province, isFetching: isLoadingProvince } = useProvince(
    Number(activeGeocontextId),
    filtersProvince
  )
  const provinceFiltered = useMemo(() => {
    const trimmed = value.trim()
    if (!trimmed) return []

    const list = (province as Provincia[] | undefined) ?? []
    const lowered = trimmed.toLowerCase()

    const filtered = list.filter((c) => c.name.toLowerCase().includes(lowered))

    return filtered.sort((a, b) => {
      const aStarts = a.name.toLowerCase().startsWith(lowered)
      const bStarts = b.name.toLowerCase().startsWith(lowered)
      if (aStarts && !bStarts) return -1
      if (!aStarts && bStarts) return 1
      return a.name.length - b.name.length
    })
  }, [value, province])

  // Sync testo con province (es. set esterno / deep link)
  useEffect(() => {
    if (!filters.province) return
    const list = (province as Provincia[] | undefined) ?? []
    const comune = list.find((c) => String(c.id) === String(filters.province))
    setValue(comune?.name || '')
  }, [filters.province, province, setValue])

  // se province torna vuoto, pulisco testo
  useEffect(() => {
    if (filters.province === '') setValue('')
  }, [filters.province, setValue])

  const clearProvincia = useCallback(() => {
    setValue('')
    setFilters({ ...filters, province: '' })
    setOpenOptions(false)
  }, [filters, setFilters, setOpenOptions, setValue])

  const centerOnProvincia = useCallback(() => {
    const list = (province as Provincia[] | undefined) ?? []
    const provincia = list.find(
      (c) => c.name.toLowerCase() === value.toLowerCase()
    )
    if (provincia && setProvinciaFilterCoords) {
      setProvinciaFilterCoords(provincia.centroid.coordinates)
    }
    setOpenOptions(false)
  }, [province, setProvinciaFilterCoords, setOpenOptions, value])

  const onSelectProvincia = useCallback(
    (provincia: Provincia) => {
      setDetail(null)
      setValue(provincia.name)
      setOpenOptions(false)
      setFilters({
        ...filters,
        province: provincia.id,
        municipality: '',
        monument_id: '',
        monument_lat: '',
        monument_lon: '',
      })
      if (setProvinciaFilterCoords) {
        setProvinciaFilterCoords(provincia.centroid.coordinates)
      }
    },
    [
      filters,
      setDetail,
      setFilters,
      setProvinciaFilterCoords,
      setOpenOptions,
      setValue,
    ]
  )

  return (
    <div className={styles.FilterComune}>
      <div className={styles.FilterLabel}>
        {t(activeGeocontext?.province_name ?? 'FIL_BOX_PROVINCE')}
      </div>

      <div className="w-100 position-relative">
        <input
          className={styles.InputComune}
          type="text"
          placeholder={t(activeGeocontext?.province_placeholder ?? '')}
          value={value}
          onChange={(e) => {
            setOpenOptions(true)
            setValue(e.target.value)
          }}
        />

        {filters.province !== '' && filters.municipality === '' && (
          <div
            title={t('FIL_BOX_CENTER_PROVINCE')}
            className={styles.PointComune}
            onClick={centerOnProvincia}
          >
            <MyLocation />
          </div>
        )}

        {value.length > 0 && (
          <div className={styles.CloseIconWrapper} onClick={clearProvincia}>
            <CloseSecondary className={styles.CloseIcon} />
          </div>
        )}
      </div>

      {openOptions &&
        value.trim().length > 0 &&
        provinceFiltered.length > 0 && (
          <div className={styles.ComuniList}>
            {provinceFiltered.map((provincia) => (
              <div
                key={provincia.code}
                className={styles.ComuneItem}
                onClick={() => onSelectProvincia(provincia)}
              >
                <div className={styles.ComuneItemTitle}>
                  <Flag className="me-2" /> {provincia.name}
                </div>
              </div>
            ))}
          </div>
        )}
      {openOptions &&
        value.trim().length > 0 &&
        provinceFiltered.length === 0 &&
        !isLoadingProvince && (
          <div className={styles.NoResults}>{t('FIL_BOX_NO_PROVINCE')}</div>
        )}
    </div>
  )
}

type ComuneAutocompleteProps = {
  value: string
  setValue: (v: string) => void
  openOptions: boolean
  setOpenOptions: (v: boolean) => void

  filters: Record<string, any>
  setFilters: (filters: any) => void
  setDetail: (detail: any) => void
  setComuneFilterCoords?: (coords: number[]) => void

  activeGeocontextId?: string | number
  activeGeocontext?: Geocontext
}

function ComuneAutocomplete({
  value,
  setValue,
  openOptions,
  setOpenOptions,
  filters,
  setFilters,
  setDetail,
  setComuneFilterCoords,
  activeGeocontextId,
  activeGeocontext,
}: ComuneAutocompleteProps) {
  const { t } = useTranslation()

  const filtersComuni = useMemo(() => {
    const trimmed = value.trim()
    const base: Record<string, any> = {}

    // ✅ cascata: prima provincia, altrimenti regione
    if (filters.province !== '') {
      base.province = filters.province
    } else if (filters.region !== '') {
      base.region = filters.region
    }

    // ✅ ricerca
    if (trimmed) {
      base.q = trimmed
    }

    // ✅ preselezione / sync
    if (!trimmed && filters.municipality !== '') {
      base.id = filters.municipality
      // (opzionale) base.q = ''  -> di solito inutile, meglio non mandarlo
    }

    return base
  }, [value, filters.municipality, filters.province, filters.region])

  const { data: comuni, isFetching: isLoadingComuni } = useComuni(
    Number(activeGeocontextId),
    filtersComuni
  )

  const comuniFiltered = useMemo(() => {
    const trimmed = value.trim()
    if (!trimmed) return []

    const list = (comuni as Comune[] | undefined) ?? []
    const lowered = trimmed.toLowerCase()

    const filtered = list.filter((c) => c.name.toLowerCase().includes(lowered))

    return filtered.sort((a, b) => {
      const aStarts = a.name.toLowerCase().startsWith(lowered)
      const bStarts = b.name.toLowerCase().startsWith(lowered)
      if (aStarts && !bStarts) return -1
      if (!aStarts && bStarts) return 1
      return a.name.length - b.name.length
    })
  }, [value, comuni])

  // Sync testo con municipality (es. set esterno / deep link)
  useEffect(() => {
    if (!filters.municipality) return
    const list = (comuni as Comune[] | undefined) ?? []
    const comune = list.find(
      (c) => String(c.id) === String(filters.municipality)
    )
    setValue(comune?.name || '')
  }, [filters.municipality, comuni, setValue])

  // se municipality torna vuoto, pulisco testo
  useEffect(() => {
    if (filters.municipality === '') setValue('')
  }, [filters.municipality, setValue])

  const clearComune = useCallback(() => {
    setValue('')
    setFilters({ ...filters, municipality: '' })
    setOpenOptions(false)
  }, [filters, setFilters, setOpenOptions, setValue])

  const centerOnComune = useCallback(() => {
    const list = (comuni as Comune[] | undefined) ?? []
    const comune = list.find(
      (c) => c.name.toLowerCase() === value.toLowerCase()
    )
    if (comune && setComuneFilterCoords) {
      setComuneFilterCoords(comune.centroid.coordinates)
    }
    setOpenOptions(false)
  }, [comuni, setComuneFilterCoords, setOpenOptions, value])

  const onSelectComune = useCallback(
    (comune: Comune) => {
      setDetail(null)
      setValue(comune.name)
      setOpenOptions(false)
      setFilters({
        ...filters,
        municipality: comune.id,
        monument_id: '',
        monument_lat: '',
        monument_lon: '',
      })
      if (setComuneFilterCoords) {
        setComuneFilterCoords(comune.centroid.coordinates)
      }
    },
    [
      filters,
      setDetail,
      setFilters,
      setComuneFilterCoords,
      setOpenOptions,
      setValue,
    ]
  )

  return (
    <div className={styles.FilterComune}>
      <div className={styles.FilterLabel}>
        {t(activeGeocontext?.municipality_name ?? 'FIL_BOX_MUNICIPALITY')}
      </div>

      <div className="w-100 position-relative">
        <input
          className={styles.InputComune}
          type="text"
          placeholder={t(activeGeocontext?.municipality_placeholder ?? '')}
          value={value}
          onChange={(e) => {
            setOpenOptions(true)
            setValue(e.target.value)
          }}
        />

        {filters.municipality !== '' && (
          <div
            title={t('FIL_BOX_CENTER_MUNICIPALITY')}
            className={styles.PointComune}
            onClick={centerOnComune}
          >
            <MyLocation />
          </div>
        )}

        {value.length > 0 && (
          <div className={styles.CloseIconWrapper} onClick={clearComune}>
            <CloseSecondary className={styles.CloseIcon} />
          </div>
        )}
      </div>

      {openOptions && value.trim().length > 0 && comuniFiltered.length > 0 && (
        <div className={styles.ComuniList}>
          {comuniFiltered.map((comune) => (
            <div
              key={comune.code}
              className={styles.ComuneItem}
              onClick={() => onSelectComune(comune)}
            >
              <div className={styles.ComuneItemTitle}>
                <Flag className="me-2" /> {comune.name}
              </div>
            </div>
          ))}
        </div>
      )}
      {openOptions &&
        value.trim().length > 0 &&
        comuniFiltered.length === 0 &&
        !isLoadingComuni && (
          <div className={styles.NoResults}>{t('FIL_BOX_NO_MUNICIPALITY')}</div>
        )}
    </div>
  )
}

type CategorySelectProps = {
  open: boolean
  setOpen: (v: boolean) => void
  filters: Record<string, any>
  setFilters: (filters: any) => void
  setDetail: (detail: any) => void
  orderedCategories: Category[]
  activeGeocontext?: any
}

function CategorySelect({
  open,
  setOpen,
  filters,
  setFilters,
  setDetail,
  orderedCategories,
  activeGeocontext,
}: CategorySelectProps) {
  const { t } = useTranslation()

  const refCategory = useDetectClickOutside({
    onTriggered: () => setOpen(false),
  })

  const selectedCategoryLabel = useMemo(() => {
    if (filters.app_category === '') return ''
    const cat = (
      activeGeocontext?.app_categories as Category[] | undefined
    )?.find((c) => String(c.id) === String(filters.app_category))
    return cat?.translation || ''
  }, [activeGeocontext, filters.app_category])

  const clearCategory = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      setFilters({ ...filters, app_category: '' })
      setOpen(false)
    },
    [filters, setFilters, setOpen]
  )

  const onSelectCategory = useCallback(
    (categoryId: string | number) => {
      setDetail(null)
      setFilters({ ...filters, app_category: categoryId })
      setOpen(false)
    },
    [filters, setDetail, setFilters, setOpen]
  )

  return (
    <div className={styles.FilterCategoria} ref={refCategory}>
      <div className={styles.FilterLabel}>{t('FIL_BOX_CATEGORY')}</div>

      <div className="w-100 position-relative">
        <div className={styles.SelectCategoria} onClick={() => setOpen(!open)}>
          {filters.app_category === '' ? (
            <div className={styles.SelectCategoriaPlaceholder}>
              {t('FIL_BOX_SELECT_CATEGORY_HINT')}
            </div>
          ) : (
            <div className={styles.SelectCategoriaText}>
              {t(selectedCategoryLabel)}
            </div>
          )}

          {filters.app_category !== '' && (
            <div className={styles.CloseIconWrapper} onClick={clearCategory}>
              <CloseSecondary className={styles.CloseIcon} />
            </div>
          )}

          {open ? (
            <ExpandLess className={styles.ExpandIcon} />
          ) : (
            <ExpandLess
              style={{ transform: 'translateY(-50%) rotate(180deg)' }}
              className={styles.ExpandIcon}
            />
          )}
        </div>

        {open && (
          <div className={styles.OptionsCategoria}>
            {orderedCategories.map((category) => {
              const isActive =
                String(filters.app_category) === String(category.id)
              return (
                <div
                  key={category.id}
                  className={classNames(styles.FilterItem, {
                    [styles.FilterItemActive]: isActive,
                  })}
                  onClick={() => onSelectCategory(category.id)}
                >
                  <div>
                    {isActive ? <CheckOrderingIcon /> : <UncheckOrderingIcon />}
                  </div>

                  <div className={styles.FilterItemTitle}>
                    <img
                      src={category.icon.icon_color}
                      alt={category.name}
                      width="13"
                      height="13"
                    />
                    <div className="ms-2 text-start">
                      {category.translation
                        ? t(category.translation)
                        : t(category.name)}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*                                  Container                                 */
/* -------------------------------------------------------------------------- */

export default function BlockFilters({
  setFilters,
  filters,
  setDetail,
  setComuneFilterCoords,
  setProvinciaFilterCoords,
  setRegioneFilterCoords,
  isResized,
  setFiltersWidth,
}: Props) {
  const { t } = useTranslation()
  const [searchComune, setSearchComune] = useState('')
  const [searchProvincia, setSearchProvincia] = useState('')
  const [searchRegione, setSearchRegione] = useState('')
  const [openOptions, setOpenOptions] = useState(false)
  const [openProvinciaOptions, setOpenProvinciaOptions] = useState(false)
  const [openRegioneOptions, setOpenRegioneOptions] = useState(false)
  const [openCategory, setOpenCategory] = useState(false)

  const activeGeocontext = getActiveGeocontext()
  const location = useLocation()

  const { user } = useAuthUser()
  const username = user ? user.username.replace('mw--', ' ') : ''

  const isListRoute = location.pathname.includes('lista')
  const isAdminInContext =
    Boolean(username) &&
    Boolean(activeGeocontext?.id) &&
    Boolean(user?.admin_geo_contexts?.includes(activeGeocontext?.id))

  const orderedCategories = useMemo(() => {
    const cats =
      (activeGeocontext?.app_categories as Category[] | undefined) ?? []
    if (!cats.length) return []
    return cats.slice().sort((a, b) => (a.order > b.order ? 1 : -1))
  }, [activeGeocontext])

  const hasAnyFilter = useMemo(() => {
    return (
      filters.app_category !== '' ||
      filters.municipality !== '' ||
      filters.province !== '' ||
      filters.region !== '' ||
      filters.in_contest !== 'true' ||
      filters.only_without_pictures !== '' ||
      filters.without_coordinates !== '' ||
      filters.only_disabled !== '' ||
      filters.last_image_before_5_years !== ''
    )
  }, [
    filters.app_category,
    filters.municipality,
    filters.province,
    filters.region,
    filters.in_contest,
    filters.only_without_pictures,
    filters.without_coordinates,
    filters.only_disabled,
    filters.last_image_before_5_years,
  ])

  const resetFilters = useCallback(() => {
    if (!hasAnyFilter) return
    setFilters(DEFAULT_FILTERS)
    setSearchComune('')
  }, [hasAnyFilter, setFilters])

  return (
    <div
      className={classNames(styles.BlockFilters, {
        // [styles.BlockFiltersClosed]: !isOpenFilters
      })}
    >
      {isResized && (
        <div
          className={styles.ButtonToggleFilters}
          onClick={() => {
            setFiltersWidth?.(340)
            // esempio: 300px quando aperto, 0 quando chiuso
          }}
        >
          {isResized ? (
            <ToggleFiltersClose aria-label={t('FIL_BOX_HIDE_FILTERS')} />
          ) : (
            <ToggleFiltersOpen aria-label={t('FIL_BOX_SHOW_FILTERS')} />
          )}
          {hasAnyFilter && <div className={styles.Badge} />}
        </div>
      )}
      {!isResized && (
        <>
          <div className={styles.TopFiltri}>
            <div className="d-flex align-items-center">
              <FiltersIcon />{' '}
              <div className={styles.FilterLabelFiltri}>
                {t('FIL_BOX_FILTER_TITLE_DESKTOP')}
              </div>
            </div>
            <div className="d-flex align-items-center">
              <div
                className={classNames({
                  [styles.ResetButton]: hasAnyFilter,
                  [styles.ResetButtonDisabled]: !hasAnyFilter,
                })}
                onClick={resetFilters}
              >
                {t('FIL_BOX_RESET')}
              </div>
              <div
                className={styles.ButtonToggleFilters}
                onClick={() => {
                  setFiltersWidth?.(50)
                }}
              >
                {isResized ? (
                  <ToggleFiltersClose aria-label={t('FIL_BOX_HIDE_FILTERS')} />
                ) : (
                  <ToggleFiltersOpen
                    onClick={() => {
                      setFiltersWidth?.(50)
                    }}
                    aria-label={t('FIL_BOX_SHOW_FILTERS')}
                  />
                )}
              </div>
            </div>
          </div>
          {activeGeocontext?.allow_region_filter && (
            <RegioneAutocomplete
              value={searchRegione}
              setValue={setSearchRegione}
              openOptions={openRegioneOptions}
              setOpenOptions={setOpenRegioneOptions}
              filters={filters}
              setFilters={setFilters}
              setDetail={setDetail}
              setRegioneFilterCoords={setRegioneFilterCoords}
              activeGeocontextId={activeGeocontext?.id}
              activeGeocontext={activeGeocontext}
            />
          )}
          {activeGeocontext?.allow_province_filter && (
            <ProvinciaAutocomplete
              value={searchProvincia}
              setValue={setSearchProvincia}
              openOptions={openProvinciaOptions}
              setOpenOptions={setOpenProvinciaOptions}
              filters={filters}
              setFilters={setFilters}
              setDetail={setDetail}
              setProvinciaFilterCoords={setProvinciaFilterCoords}
              activeGeocontextId={activeGeocontext?.id}
              activeGeocontext={activeGeocontext}
            />
          )}

          <ComuneAutocomplete
            value={searchComune}
            setValue={setSearchComune}
            openOptions={openOptions}
            setOpenOptions={setOpenOptions}
            filters={filters}
            setFilters={setFilters}
            setDetail={setDetail}
            setComuneFilterCoords={setComuneFilterCoords}
            activeGeocontextId={activeGeocontext?.id}
            activeGeocontext={activeGeocontext}
          />

          <CategorySelect
            open={openCategory}
            setOpen={setOpenCategory}
            filters={filters}
            setFilters={setFilters}
            setDetail={setDetail}
            orderedCategories={orderedCategories}
            activeGeocontext={activeGeocontext}
          />

          {activeGeocontext?.show_button_out_of_contest && (
            <div className={styles.Filter}>
              <div className={styles.FilterTitle}>
                {t('FIL_BOX_ALSO_OUT_CONTEST')}
              </div>
              <div className="d-flex align-items-center">
                <ReactSwitch
                  offColor="#D0DFE4"
                  size={15}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  onColor="#40BAEC"
                  onChange={(checked) => {
                    setFilters({
                      ...filters,
                      in_contest: checked ? '' : 'true',
                    })
                  }}
                  checked={filters.in_contest !== 'true'}
                />
              </div>
            </div>
          )}

          <div className={styles.Filter}>
            <div className={styles.FilterTitle}>
              {t('FIL_BOX_ONLY_WITHOUT_PHOTOS')}
            </div>
            <div className="d-flex align-items-center">
              <ReactSwitch
                offColor="#D0DFE4"
                size={15}
                checkedIcon={false}
                uncheckedIcon={false}
                onColor="#40BAEC"
                onChange={(checked) => {
                  setFilters({
                    ...filters,
                    only_without_pictures: checked ? 'true' : '',
                  })
                }}
                checked={filters.only_without_pictures === 'true'}
              />
            </div>
          </div>

          {isListRoute && (
            <div className={styles.Filter}>
              <div className={styles.FilterTitle}>
                {t('FIL_BOX_WITHOUT_COORD')}
              </div>
              <div className="d-flex align-items-center">
                <ReactSwitch
                  offColor="#D0DFE4"
                  size={15}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  onColor="#40BAEC"
                  onChange={(checked) => {
                    setFilters({
                      ...filters,
                      without_coordinates: checked ? 'true' : '',
                    })
                  }}
                  checked={filters.without_coordinates === 'true'}
                />
              </div>
            </div>
          )}

          <div className={styles.Filter}>
            <div className={styles.FilterTitle}>
              {t('FIL_BOX_LAST_IMAGE_BEFORE_5_YEARS')}
            </div>
            <div className="d-flex align-items-center">
              <ReactSwitch
                offColor="#D0DFE4"
                size={15}
                checkedIcon={false}
                uncheckedIcon={false}
                onColor="#40BAEC"
                onChange={(checked) => {
                  setFilters({
                    ...filters,
                    last_image_before_5_years: checked ? 'true' : '',
                  })
                }}
                checked={filters.last_image_before_5_years === 'true'}
              />
            </div>
          </div>

          {isAdminInContext && (
            <div className={styles.Filter}>
              <div className={styles.FilterTitle}>
                {t('FIL_BOX_DISABLED_ENTRIES')}
              </div>
              <div className="d-flex align-items-center">
                <ReactSwitch
                  offColor="#D0DFE4"
                  size={15}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  onColor="#CF4130"
                  onChange={(checked) => {
                    setFilters({
                      ...filters,
                      only_disabled: checked ? 'true' : '',
                    })
                  }}
                  checked={filters.only_disabled === 'true'}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
